import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LIST_STAFF, GET_VIDEO_CONFERENCE } from '../../graphql/queries';
import { gql } from '@apollo/client';
import toast from 'react-hot-toast';
import { ParticipantSelector, Participant } from './ParticipantSelector';

// GraphQL mutation for updating video conference
const UPDATE_VIDEO_CONFERENCE = gql`
  mutation UpdateVideoConference($id: ID!, $input: UpdateVideoConferenceInput!) {
    updateVideoConference(id: $id, input: $input) {
      id
      title
      status
      scheduledStartTime
      scheduledEndTime
      joinUrl
      participants {
        id
        type
        referenceId
        name
        email
        phone
        notifyEmail
        notifyPush
        notifySms
      }
      waitingRoomEnabled
      reminderTimes
      isRecordingEnabled
      attendeeIds
    }
  }
`;

// List video conferences query for refetch
const LIST_VIDEO_CONFERENCES = gql`
  query ListVideoConferences($status: ConferenceStatus, $limit: Int, $nextToken: String) {
    listVideoConferences(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        status
      }
      nextToken
    }
  }
`;

// Wizard steps
type WizardStep = 'participants' | 'details' | 'notifications' | 'review';

const WIZARD_STEPS: { key: WizardStep; label: string; number: number }[] = [
  { key: 'participants', label: 'Participants', number: 1 },
  { key: 'details', label: 'Details', number: 2 },
  { key: 'notifications', label: 'Notifications', number: 3 },
  { key: 'review', label: 'Review', number: 4 },
];

// Reminder time options (in minutes)
const REMINDER_OPTIONS = [
  { value: 10080, label: '1 week' },
  { value: 4320, label: '3 days' },
  { value: 1440, label: '1 day' },
  { value: 720, label: '12 hours' },
  { value: 360, label: '6 hours' },
  { value: 180, label: '3 hours' },
  { value: 60, label: '1 hour' },
  { value: 30, label: '30 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 5, label: '5 minutes' },
  { value: 0, label: 'At start time' },
];

// Default reminder times
const DEFAULT_REMINDER_TIMES = [1440, 60, 15]; // 24h, 1h, 15min

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface ListStaffData {
  listStaff: StaffMember[];
}

interface VideoConferenceData {
  getVideoConference: {
    id: string;
    title: string;
    description?: string;
    status: string;
    scheduledStartTime: string;
    scheduledEndTime: string;
    hostId: string;
    isRecordingEnabled: boolean;
    waitingRoomEnabled?: boolean;
    reminderTimes?: number[];
    attendeeIds?: string[];
    participants?: Array<{
      id: string;
      type: 'CLIENT' | 'INQUIRY' | 'GUEST';
      referenceId?: string;
      name: string;
      email?: string;
      phone?: string;
      notifyEmail: boolean;
      notifyPush: boolean;
      notifySms: boolean;
    }>;
    // Legacy fields for backward compatibility
    clientId?: string;
    client?: { id: string; firstName: string; lastName: string; email?: string };
    inquiryId?: string;
    inquiry?: { id: string; firstName: string; lastName: string; email?: string };
    guestName?: string;
    guestEmail?: string;
    guestPhone?: string;
  };
}

interface EditConferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: () => void;
  conferenceId: string;
  currentUserId: string;
  currentUserRole: string;
}

export function EditConferenceModal({
  isOpen,
  onClose,
  onUpdated,
  conferenceId,
  currentUserId,
  currentUserRole,
}: EditConferenceModalProps) {
  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('participants');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  // Participants state
  const [participants, setParticipants] = useState<Participant[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    isRecordingEnabled: false,
    waitingRoomEnabled: true, // Always enabled
    reminderTimes: DEFAULT_REMINDER_TIMES,
    attendeeIds: [] as string[],
  });

  // Queries
  const { data: conferenceData, loading: conferenceLoading, error: conferenceError } = useQuery<VideoConferenceData>(
    GET_VIDEO_CONFERENCE,
    {
      variables: { id: conferenceId },
      skip: !isOpen || !conferenceId,
      fetchPolicy: 'network-only',
    }
  );

  const { data: staffData } = useQuery<ListStaffData>(LIST_STAFF);
  const staff: StaffMember[] = staffData?.listStaff || [];

  const [updateConference] = useMutation(UPDATE_VIDEO_CONFERENCE, {
    refetchQueries: [{ query: LIST_VIDEO_CONFERENCES, variables: { limit: 200 } }],
  });


  // Get current timezone
  const timezone = useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, []);

  // Check permission - only host or admin can edit
  const canEdit = useMemo(() => {
    if (!conferenceData?.getVideoConference) return false;
    const conference = conferenceData.getVideoConference;
    const isHost = conference.hostId === currentUserId;
    const isAdmin = currentUserRole === 'ADMIN';
    return isHost || isAdmin;
  }, [conferenceData, currentUserId, currentUserRole]);

  // Initialize form with existing conference data
  useEffect(() => {
    if (isOpen && conferenceData?.getVideoConference) {
      const conference = conferenceData.getVideoConference;

      // Check permission
      if (!canEdit) {
        setPermissionError('You do not have permission to edit this conference. Only the host or an admin can edit.');
        return;
      }

      setPermissionError(null);

      // Convert ISO datetime to local datetime-local format
      const formatDateTimeLocal = (isoString: string) => {
        const date = new Date(isoString);
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - offset * 60 * 1000);
        return localDate.toISOString().slice(0, 16);
      };

      // Initialize participants from conference data
      let initialParticipants: Participant[] = [];

      // Use new participants array if available
      if (conference.participants && conference.participants.length > 0) {
        initialParticipants = conference.participants.map((p) => ({
          id: p.id,
          type: p.type,
          referenceId: p.referenceId,
          name: p.name,
          email: p.email,
          phone: p.phone,
          notifyEmail: p.notifyEmail,
          notifyPush: p.notifyPush,
          notifySms: p.notifySms,
        }));
      } else {
        // Legacy format: construct from single-participant fields
        if (conference.clientId && conference.client) {
          initialParticipants.push({
            id: `legacy-client-${conference.clientId}`,
            type: 'CLIENT',
            referenceId: conference.clientId,
            name: `${conference.client.firstName} ${conference.client.lastName}`,
            email: conference.client.email,
            notifyEmail: true,
            notifyPush: true,
            notifySms: false,
          });
        } else if (conference.inquiryId && conference.inquiry) {
          initialParticipants.push({
            id: `legacy-inquiry-${conference.inquiryId}`,
            type: 'INQUIRY',
            referenceId: conference.inquiryId,
            name: `${conference.inquiry.firstName} ${conference.inquiry.lastName}`,
            email: conference.inquiry.email,
            notifyEmail: true,
            notifyPush: false,
            notifySms: false,
          });
        } else if (conference.guestName) {
          initialParticipants.push({
            id: `legacy-guest-${conference.id}`,
            type: 'GUEST',
            name: conference.guestName,
            email: conference.guestEmail,
            phone: conference.guestPhone,
            notifyEmail: !!conference.guestEmail,
            notifyPush: false,
            notifySms: false,
          });
        }
      }

      setParticipants(initialParticipants);

      setFormData({
        title: conference.title || '',
        description: conference.description || '',
        startTime: formatDateTimeLocal(conference.scheduledStartTime),
        endTime: formatDateTimeLocal(conference.scheduledEndTime),
        isRecordingEnabled: conference.isRecordingEnabled || false,
        waitingRoomEnabled: true, // Always enabled
        reminderTimes: conference.reminderTimes || DEFAULT_REMINDER_TIMES,
        attendeeIds: conference.attendeeIds || [],
      });

      setCurrentStep('participants');
      setIsSubmitting(false);
      setSubmitSuccess(false);
    }
  }, [isOpen, conferenceData, canEdit]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep('participants');
      setIsSubmitting(false);
      setSubmitSuccess(false);
      setPermissionError(null);
    }
  }, [isOpen]);

  // Navigation handlers
  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 'participants':
        return participants.length > 0;
      case 'details':
        return !!(
          formData.title.trim() &&
          formData.startTime &&
          formData.endTime &&
          new Date(formData.endTime) > new Date(formData.startTime)
        );
      case 'notifications':
        return true;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const goToNextStep = () => {
    const currentIndex = WIZARD_STEPS.findIndex((s) => s.key === currentStep);
    if (currentIndex < WIZARD_STEPS.length - 1) {
      setCurrentStep(WIZARD_STEPS[currentIndex + 1].key);
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = WIZARD_STEPS.findIndex((s) => s.key === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(WIZARD_STEPS[currentIndex - 1].key);
    }
  };

  const goToStep = (step: WizardStep) => {
    const targetIndex = WIZARD_STEPS.findIndex((s) => s.key === step);
    const currentIndex = WIZARD_STEPS.findIndex((s) => s.key === currentStep);

    // Can only go back or to current step
    if (targetIndex <= currentIndex) {
      setCurrentStep(step);
    }
  };

  // Staff attendee toggle
  const handleAttendeeToggle = (staffId: string) => {
    setFormData((prev) => ({
      ...prev,
      attendeeIds: prev.attendeeIds.includes(staffId)
        ? prev.attendeeIds.filter((id) => id !== staffId)
        : [...prev.attendeeIds, staffId],
    }));
  };

  // Reminder toggle
  const handleReminderToggle = (minutes: number) => {
    setFormData((prev) => ({
      ...prev,
      reminderTimes: prev.reminderTimes.includes(minutes)
        ? prev.reminderTimes.filter((t) => t !== minutes)
        : [...prev.reminderTimes, minutes].sort((a, b) => b - a),
    }));
  };

  // Update participant notification settings
  const updateParticipantNotification = (
    participantId: string,
    field: 'notifyEmail' | 'notifyPush' | 'notifySms',
    value: boolean
  ) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === participantId ? { ...p, [field]: value } : p))
    );
  };


  // Submit handler
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const participantsInput = participants.map((p) => ({
        type: p.type,
        referenceId: p.referenceId,
        name: p.name,
        email: p.email,
        phone: p.phone,
        notifyEmail: p.notifyEmail,
        notifyPush: p.notifyPush,
        notifySms: p.notifySms,
      }));

      const input = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        scheduledStartTime: new Date(formData.startTime).toISOString(),
        scheduledEndTime: new Date(formData.endTime).toISOString(),
        isRecordingEnabled: formData.isRecordingEnabled,
        waitingRoomEnabled: formData.waitingRoomEnabled,
        reminderTimes: formData.reminderTimes.length > 0 ? formData.reminderTimes : undefined,
        attendeeIds: formData.attendeeIds.length > 0 ? formData.attendeeIds : undefined,
        participants: participantsInput,
      };

      await updateConference({ variables: { id: conferenceId, input } });
      setSubmitSuccess(true);
      toast.success('Video conference updated successfully!');
      onUpdated?.();
    } catch (error: any) {
      console.error('Error updating conference:', error);
      // Check for authorization error
      if (error.message?.includes('Unauthorized') || error.message?.includes('permission')) {
        setPermissionError(error.message || 'You do not have permission to edit this conference.');
      } else {
        toast.error(error.message || 'Failed to update conference');
      }
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Loading state
  if (conferenceLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red mb-4"></div>
            <p className="text-gray-600">Loading conference details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (conferenceError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Conference</h2>
            <p className="text-gray-600 mb-6">{conferenceError.message || 'Failed to load conference details.'}</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  // Permission error state
  if (permissionError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Permission Denied</h2>
            <p className="text-gray-600 mb-6">{permissionError}</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  // Success screen
  if (submitSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Conference Updated!</h2>
            <p className="text-gray-600 mb-6">
              Your video conference has been updated successfully. Participants will receive update notifications.
            </p>
            <Button onClick={onClose} className="w-full">
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">Edit Video Conference</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Indicator */}
        <StepIndicator steps={WIZARD_STEPS} currentStep={currentStep} onStepClick={goToStep} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 'participants' && (
            <StepParticipants participants={participants} onParticipantsChange={setParticipants} />
          )}

          {currentStep === 'details' && (
            <StepDetails
              formData={formData}
              setFormData={setFormData}
              staff={staff}
              onAttendeeToggle={handleAttendeeToggle}
              onReminderToggle={handleReminderToggle}
              timezone={timezone}
            />
          )}

          {currentStep === 'notifications' && (
            <StepNotifications participants={participants} onUpdateNotification={updateParticipantNotification} />
          )}

          {currentStep === 'review' && (
            <StepReview participants={participants} formData={formData} staff={staff} timezone={timezone} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            {currentStep !== 'participants' && (
              <Button type="button" variant="outline" onClick={goToPreviousStep} disabled={isSubmitting}>
                ← Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            {currentStep !== 'review' ? (
              <Button type="button" onClick={goToNextStep} disabled={!canGoNext()}>
                Next →
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Updating...
                  </span>
                ) : (
                  'Update Conference'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step Indicator Component
interface StepIndicatorProps {
  steps: { key: WizardStep; label: string; number: number }[];
  currentStep: WizardStep;
  onStepClick: (step: WizardStep) => void;
}

function StepIndicator({ steps, currentStep, onStepClick }: StepIndicatorProps) {
  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = step.key === currentStep;
          const isClickable = index <= currentIndex;

          return (
            <div key={step.key} className="flex items-center flex-1">
              <button
                type="button"
                onClick={() => isClickable && onStepClick(step.key)}
                disabled={!isClickable}
                className={`flex items-center gap-2 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                      ? 'bg-aalaw-red text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:block ${isCurrent ? 'text-gray-900' : 'text-gray-500'}`}
                >
                  {step.label}
                </span>
              </button>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${index < currentIndex ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Step 1: Participants
interface StepParticipantsProps {
  participants: Participant[];
  onParticipantsChange: (participants: Participant[]) => void;
}

function StepParticipants({ participants, onParticipantsChange }: StepParticipantsProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Edit Participants</h3>
        <p className="text-sm text-gray-500">
          Add or remove external participants for this conference. You can add clients, inquiries, or guests.
        </p>
      </div>

      <ParticipantSelector
        selectedParticipants={participants}
        onParticipantsChange={onParticipantsChange}
        maxParticipants={10}
      />

      {participants.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">Please add at least one participant to continue.</p>
        </div>
      )}
    </div>
  );
}

// Form data type
interface ConferenceFormData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  isRecordingEnabled: boolean;
  waitingRoomEnabled: boolean;
  reminderTimes: number[];
  attendeeIds: string[];
}

// Step 2: Details
interface StepDetailsProps {
  formData: ConferenceFormData;
  setFormData: React.Dispatch<React.SetStateAction<ConferenceFormData>>;
  staff: StaffMember[];
  onAttendeeToggle: (staffId: string) => void;
  onReminderToggle: (minutes: number) => void;
  timezone: string;
}

function StepDetails({
  formData,
  setFormData,
  staff,
  onAttendeeToggle,
  onReminderToggle,
  timezone,
}: StepDetailsProps) {
  const timeError = useMemo(() => {
    if (formData.startTime && formData.endTime) {
      if (new Date(formData.endTime) <= new Date(formData.startTime)) {
        return 'End time must be after start time';
      }
    }
    return null;
  }, [formData.startTime, formData.endTime]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Conference Details</h3>
        <p className="text-sm text-gray-500">Update the details for your video conference.</p>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData((prev: ConferenceFormData) => ({ ...prev, title: e.target.value }))}
          placeholder="e.g., Initial Consultation, Case Review"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData((prev: ConferenceFormData) => ({ ...prev, description: e.target.value }))}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
          placeholder="Add notes or agenda for this conference"
        />
      </div>

      {/* Date and Time */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date & Time <span className="text-red-500">*</span>
          <span className="ml-2 text-xs text-gray-400 font-normal">({timezone})</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start</label>
            <input
              type="datetime-local"
              value={formData.startTime}
              onChange={(e) => setFormData((prev: ConferenceFormData) => ({ ...prev, startTime: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End</label>
            <input
              type="datetime-local"
              value={formData.endTime}
              onChange={(e) => setFormData((prev: ConferenceFormData) => ({ ...prev, endTime: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent ${
                timeError ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            />
          </div>
        </div>
        {timeError && <p className="mt-1 text-sm text-red-600">{timeError}</p>}
      </div>

      {/* Recording Toggle */}
      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
        <div>
          <p className="font-medium text-gray-900">Enable Recording</p>
          <p className="text-sm text-gray-500">Record this conference for later review</p>
        </div>
        <ToggleSwitch
          checked={formData.isRecordingEnabled}
          onChange={(checked) => setFormData((prev: ConferenceFormData) => ({ ...prev, isRecordingEnabled: checked }))}
        />
      </div>

      {/* Waiting Room Info (always enabled) */}
      <div className="flex items-center justify-between p-4 border border-blue-200 rounded-lg bg-blue-50">
        <div>
          <p className="font-medium text-blue-900">Waiting Room Enabled</p>
          <p className="text-sm text-blue-700">Participants will wait until you start the meeting</p>
        </div>
        <div className="text-blue-600">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      {/* Reminder Times */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Reminder Notifications</label>
        <p className="text-xs text-gray-500 mb-3">Select when to send reminder notifications to participants</p>

        {/* Selected reminders as chips */}
        {formData.reminderTimes.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {[...formData.reminderTimes]
              .sort((a, b) => b - a)
              .map((minutes) => {
                const option = REMINDER_OPTIONS.find((o) => o.value === minutes);
                return (
                  <span
                    key={minutes}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-aalaw-red/10 text-aalaw-red rounded-full text-sm"
                  >
                    {option?.label || `${minutes} min`}
                    <button
                      type="button"
                      onClick={() => onReminderToggle(minutes)}
                      className="hover:bg-aalaw-red/20 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                );
              })}
          </div>
        )}

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {REMINDER_OPTIONS.map((option) => {
            const isSelected = formData.reminderTimes.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onReminderToggle(option.value)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  isSelected
                    ? 'bg-aalaw-red text-white border-aalaw-red'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-aalaw-red'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {formData.reminderTimes.length === 0 && (
          <p className="mt-2 text-xs text-amber-600">
            No reminders selected. Participants won't receive reminder notifications.
          </p>
        )}
      </div>

      {/* Staff Attendees */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Additional Staff Attendees (Optional)</label>
        <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto">
          {staff.length === 0 ? (
            <p className="text-sm text-gray-500">Loading staff...</p>
          ) : (
            <div className="space-y-2">
              {staff.map((s) => (
                <label key={s.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.attendeeIds.includes(s.id)}
                    onChange={() => onAttendeeToggle(s.id)}
                    className="rounded border-gray-300 text-aalaw-red focus:ring-aalaw-red"
                  />
                  <span className="text-sm text-gray-900">
                    {s.firstName} {s.lastName} ({s.role})
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Step 3: Notifications
interface StepNotificationsProps {
  participants: Participant[];
  onUpdateNotification: (
    participantId: string,
    field: 'notifyEmail' | 'notifyPush' | 'notifySms',
    value: boolean
  ) => void;
}

function StepNotifications({ participants, onUpdateNotification }: StepNotificationsProps) {
  // Check if push is available for a participant
  const canReceivePush = (participant: Participant): boolean => {
    // Guests cannot receive push notifications
    if (participant.type === 'GUEST') return false;
    // Clients can receive push
    if (participant.type === 'CLIENT') return true;
    // Inquiries can only receive push if they have an account (indicated by notifyPush being initially true)
    return participant.type === 'INQUIRY';
  };

  // Get notification availability message
  const getNotificationInfo = (participant: Participant): string => {
    if (participant.type === 'GUEST') {
      return 'Guests can only receive email notifications';
    }
    if (participant.type === 'INQUIRY') {
      return 'Push notifications available only if inquiry has an app account';
    }
    return 'All notification methods available';
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Notification Preferences</h3>
        <p className="text-sm text-gray-500">
          Configure how each participant will be notified about this conference update.
        </p>
      </div>

      <div className="space-y-4">
        {participants.map((participant) => {
          const pushAvailable = canReceivePush(participant);
          const isGuest = participant.type === 'GUEST';

          return (
            <div key={participant.id} className="p-4 border border-gray-200 rounded-lg space-y-3">
              {/* Participant header */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="text-lg">
                    {participant.type === 'CLIENT' ? '👤' : participant.type === 'INQUIRY' ? '📋' : '🔗'}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{participant.name}</p>
                  <p className="text-xs text-gray-500">
                    {participant.type.charAt(0) + participant.type.slice(1).toLowerCase()}
                    {participant.email && ` • ${participant.email}`}
                  </p>
                </div>
              </div>

              {/* Info message */}
              <p className="text-xs text-gray-500 italic">{getNotificationInfo(participant)}</p>

              {/* Notification options */}
              <div className="grid grid-cols-3 gap-3">
                {/* Email */}
                <label className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={participant.notifyEmail}
                    onChange={(e) => onUpdateNotification(participant.id, 'notifyEmail', e.target.checked)}
                    className="rounded border-gray-300 text-aalaw-red focus:ring-aalaw-red"
                  />
                  <span className="text-sm text-gray-700">📧 Email</span>
                </label>

                {/* Push */}
                <div className="relative group">
                  <label
                    className={`flex items-center gap-2 p-2 border rounded-lg ${
                      pushAvailable && !isGuest
                        ? 'border-gray-200 cursor-pointer hover:bg-gray-50'
                        : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={participant.notifyPush && pushAvailable && !isGuest}
                      onChange={(e) => onUpdateNotification(participant.id, 'notifyPush', e.target.checked)}
                      disabled={!pushAvailable || isGuest}
                      className="rounded border-gray-300 text-aalaw-red focus:ring-aalaw-red disabled:opacity-50"
                    />
                    <span className="text-sm text-gray-700">🔔 Push</span>
                  </label>
                  {(!pushAvailable || isGuest) && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      {isGuest ? 'Not available for guests' : 'Requires app account'}
                    </div>
                  )}
                </div>

                {/* SMS */}
                <div className="relative group">
                  <label className="flex items-center gap-2 p-2 border border-gray-100 rounded-lg bg-gray-50 cursor-not-allowed opacity-60">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                      className="rounded border-gray-300 text-aalaw-red focus:ring-aalaw-red disabled:opacity-50"
                    />
                    <span className="text-sm text-gray-700">📱 SMS</span>
                  </label>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                    Coming soon
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Step 4: Review
interface StepReviewProps {
  participants: Participant[];
  formData: ConferenceFormData;
  staff: StaffMember[];
  timezone: string;
}

function StepReview({ participants, formData, staff, timezone }: StepReviewProps) {
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatReminderTimes = (times: number[]) => {
    if (times.length === 0) return 'No reminders';
    return [...times]
      .sort((a, b) => b - a)
      .map((minutes) => {
        const option = REMINDER_OPTIONS.find((o) => o.value === minutes);
        return option?.label || `${minutes} min`;
      })
      .join(', ');
  };

  const selectedStaff = staff.filter((s) => formData.attendeeIds.includes(s.id));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Review & Confirm</h3>
        <p className="text-sm text-gray-500">Please review the updated conference details before saving.</p>
      </div>

      {/* Conference Details */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-gray-900">Conference Details</h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Title</p>
            <p className="text-sm font-medium text-gray-900">{formData.title || 'Not set'}</p>
          </div>

          {formData.description && (
            <div className="sm:col-span-2">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Description</p>
              <p className="text-sm text-gray-900">{formData.description}</p>
            </div>
          )}

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Start Time</p>
            <p className="text-sm text-gray-900">{formatDateTime(formData.startTime)}</p>
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">End Time</p>
            <p className="text-sm text-gray-900">{formatDateTime(formData.endTime)}</p>
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Timezone</p>
            <p className="text-sm text-gray-900">{timezone}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              formData.isRecordingEnabled ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {formData.isRecordingEnabled ? '🔴 Recording Enabled' : '⚪ Recording Disabled'}
          </span>
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
          >
            🚪 Waiting Room Enabled
          </span>
        </div>
      </div>

      {/* Participants */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-gray-900">Participants ({participants.length})</h4>
        <div className="space-y-2">
          {participants.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {p.type === 'CLIENT' ? '👤' : p.type === 'INQUIRY' ? '📋' : '🔗'}
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.type.charAt(0) + p.type.slice(1).toLowerCase()}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {p.notifyEmail && <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">📧</span>}
                {p.notifyPush && <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">🔔</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Staff Attendees */}
      {selectedStaff.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-gray-900">Staff Attendees ({selectedStaff.length})</h4>
          <div className="flex flex-wrap gap-2">
            {selectedStaff.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-full text-sm"
              >
                {s.firstName} {s.lastName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Reminders */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <h4 className="font-medium text-gray-900">Reminder Notifications</h4>
        <p className="text-sm text-gray-600">{formatReminderTimes(formData.reminderTimes)}</p>
      </div>
    </div>
  );
}

// Toggle Switch Component
interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-aalaw-red peer-disabled:opacity-50"></div>
    </label>
  );
}

export default EditConferenceModal;
